import { AutoCompleteModel } from 'models/AutoCompleteModel';
import { ISearchItem } from 'models/ISearchItem';

/**
 * Transforms the search into an irregular/ragged matrix, representing the layout
 * The arrays can have different lengts
 */
export const getSearchMatrix = ({
  suggestion,
  useMultiColumnlayout,
  showResultView
}: Pick<AutoCompleteModel, 'suggestion'> & {
  useMultiColumnlayout: boolean;
  showResultView: boolean;
}): ISearchItem[][] => {
  if (!showResultView) {
    return [[...suggestion.latestSearches, ...suggestion.popularSearchPhrases]];
  }

  let matrix: ISearchItem[][];

  if (useMultiColumnlayout) {
    matrix = [
      [
        ...suggestion.suggestedCategories,
        ...suggestion.suggestedBrandCategories
          .map(b => [b, ...b.categories])
          .reduce((a, b) => [...a, ...b], []),
        ...suggestion.suggestedPhrases
      ],
      suggestion.suggestedProducts
    ].filter(col => col.length > 0);
  } else {
    matrix = [
      [
        ...suggestion.suggestedCategories,
        ...suggestion.suggestedBrandCategories,
        ...suggestion.suggestedPhrases,
        ...suggestion.suggestedProducts
      ]
    ];
  }

  if (matrix.every(col => col.length === 0)) {
    matrix = [suggestion.suggestedDidYouMean];
  }

  return matrix;
};

interface CalculateNextItemOptions {
  matrix: unknown[][];
  currentCol: number;
  currentRow: number;
  direction: 'up' | 'down' | 'left' | 'right';
}

/**
 * Calculates the next index for the search irregular matrix.
 *
 * currentCol is expected to be a number between 0 and n
 * currentRow is expected to be a number between -1 and n
 *
 * if currentRow === -1 and navigation is down, it acts as if
 * we enter from upwards, return 0 for nextRow
 *
 * If the currentRow/currentCol are at the edge of the matrix,
 * it flips over to the other edge.
 *
 * Resets the row on columns navigation to 0.
 *
 */
export const calculateNextItemIndex = ({
  matrix,
  currentCol,
  currentRow,
  direction
}: CalculateNextItemOptions) => {
  if (direction === 'up' || direction === 'down') {
    return {
      nextRow: getNextRow(direction, currentRow, matrix[currentCol].length),
      nextCol: currentCol
    };
  }

  if (matrix.length === 1) {
    return {
      nextRow: currentRow,
      nextCol: currentCol
    };
  }

  return {
    nextRow: 0,
    nextCol: getNextColumn(direction, currentCol, matrix.length)
  };
};

const getNextRow = (direction: 'up' | 'down', currentRow: number, numberOfRows: number): number => {
  const firstRow = 0;
  const lastRow = numberOfRows - 1;
  const nextRow = currentRow + 1;
  const prevRow = currentRow - 1;
  if (currentRow === -1) {
    return direction === 'down' ? firstRow : lastRow;
  }
  if (direction === 'up') {
    return currentRow === firstRow ? lastRow : prevRow;
  }

  return currentRow === lastRow ? firstRow : nextRow;
};

const getNextColumn = (
  direction: 'right' | 'left',
  currentCol: number,
  numberOfCols: number
): number => {
  const firstCol = 0;
  const lastCol = numberOfCols - 1;
  if (direction === 'right') {
    const nextCol = currentCol + 1;
    return currentCol === lastCol ? firstCol : nextCol;
  }

  const prevCol = currentCol - 1;
  return currentCol === firstCol ? lastCol : prevCol;
};
